/**
 * useIndustryProfile — fetches the resolved ROI profile for the
 * effective org (impersonated org for super admins, else the user's
 * home org). Re-fetches when the effective org changes.
 *
 * Returns `{ profile, loading }`. `profile.industry_label` is null
 * when the org has no industry tagged (falls back to global defaults).
 */
import { useState, useEffect } from 'react';
import secureStorage from '../lib/secureStorage';
import authedFetch from '../lib/authedFetch';
import logger from '../lib/logger';

export default function useIndustryProfile(orgId) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [state, setState] = useState({ profile: null, loading: false });

  useEffect(() => {
    if (!orgId) {
      setState({ profile: null, loading: false });
      return;
    }
    let mounted = true;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const res = await authedFetch(`${API_URL}/api/organizations/${orgId}/roi-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        if (res.ok) {
          const data = await res.json();
          setState({ profile: data.resolved, loading: false });
        } else {
          setState({ profile: null, loading: false });
        }
      } catch (e) {
        logger.warn('useIndustryProfile fetch failed', e);
        if (mounted) setState({ profile: null, loading: false });
      }
    })();
    return () => { mounted = false; };
  }, [orgId, API_URL]);

  return state;
}
